import {Injectable} from '@angular/core';
import {environment} from '@env';
import type {Metric} from 'web-vitals';
import {route_paths} from '@app/shared/constants/languages_data';
import {LngKeys} from '@app/shared/enums';

type AnalyticsWindow = Window & {gtag?: (...args: unknown[]) => void};

@Injectable({providedIn: 'root'})
export class WebVitalsService {
  private started = false;

  start(): void {
    if (this.started || environment.env !== 'production' || Math.random() >= 0.1) return;
    this.started = true;
    // Capture the document context once: native CWV metrics span SPA navigations.
    const language = document.documentElement.lang;
    const parts = location.pathname.split('/').filter(Boolean);
    if (parts[0] === language) parts.shift();
    const pathname = location.pathname.replace(/\/$/, '');
    const configuredPage = Object.entries(route_paths[language as LngKeys] || route_paths.en)
      .find(([, path]) => path.replace(/\/$/, '') === pathname)?.[0];
    const pageTemplate = configuredPage || (['resume-examples', 'career-blog'].includes(parts[0]) ? parts[0] : 'other');
    const report = (metric: Metric, attribution: Record<string, string | number | undefined> = {}) => {
      // Never send document content, account identifiers, raw selectors, or URL parameters.
      (window as AnalyticsWindow).gtag?.('event', 'web_vital', {
        metric_name: metric.name,
        value: metric.value,
        metric_delta: metric.delta,
        metric_id: metric.id,
        metric_rating: metric.rating,
        navigation_type: metric.navigationType,
        page_template: pageTemplate,
        language,
        viewport_class: window.innerWidth < 768 ? 'small' : 'large',
        release: 'cwv-2026-09-30',
        ...attribution,
        non_interaction: true
      });
    };
    void import('web-vitals/attribution').then(({onCLS, onLCP, onINP}) => {
      onCLS(metric => report(metric, {
        shift_component: metric.attribution.largestShiftTarget,
        shift_time: metric.attribution.largestShiftTime
      }), {generateTarget: node => {
        const element = node instanceof Element ? node : node?.parentElement;
        return ['sf-header', 'sf-home-page', 'sf-resume-examples-section', 'sf-templates-section', 'sf-footer']
          .find(selector => element?.closest(selector)) || 'other';
      }});
      onLCP(report);
      onINP(report);
    }).catch(() => { this.started = false; });
  }
}
